<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useBrandsStore } from "@/stores/brands";
import { useBrandSwitcherStore } from "@/stores/brandSwitcher";
import { useUserBrandsStore } from "@/stores/userBrands";

/**
 * Topbar dropdown that scopes every admin API call to the selected
 * brand. Visible to anyone who can read the brand registry; the
 * choice is purely presentational from the GUI's perspective —
 * server-side, it stamps `X-Brand-Code` on every authFetch request
 * so the BrandContextMiddleware on `/api/v1/*` filters accordingly.
 *
 * "Kaikki brändit" (null) is the default and corresponds to admin /
 * cross-brand scope. When the calling user has a non-empty
 * UserBrand allowlist, the dropdown only shows the brands they're
 * allowed into; "Kaikki brändit" remains as the explicit cross-brand
 * choice (the BrandAccessGuard allows admin scope for restricted
 * users so dashboards still aggregate over their permitted brands).
 */
const brands = useBrandsStore();
const switcher = useBrandSwitcherStore();
const userBrands = useUserBrandsStore();

const options = computed(() => {
  const allowed = userBrands.mine;
  if (!allowed || allowed.length === 0) return brands.activeBrands;
  const allowedIds = new Set(allowed.map((u) => u.brandId));
  return brands.activeBrands.filter((b) => allowedIds.has(b.id));
});

const value = computed({
  get: () => switcher.selectedCode ?? "",
  set: (v: string) => {
    switcher.select(v.length === 0 ? null : v);
    // Force a hard reload so every cached store re-fetches with the
    // new brand scope. Cheaper than wiring a refresh event into
    // every store; the user changes brand context infrequently.
    window.location.reload();
  },
});

onMounted(() => {
  if (brands.list.length === 0) void brands.fetchList();
  void userBrands.fetchMine();
});
</script>

<template>
  <label v-if="options.length > 0" class="brand-switcher">
    <span class="visually-hidden">Brändi</span>
    <select v-model="value" :title="'Brändi-rajaus'">
      <option value="">Kaikki brändit</option>
      <option
        v-for="b in options"
        :key="b.id"
        :value="b.code"
      >
        {{ b.code }}
      </option>
    </select>
  </label>
</template>

<style scoped>
.brand-switcher select {
  background: transparent;
  color: inherit;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  cursor: pointer;
}
.brand-switcher select:hover { border-color: #9ca3af; }

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
